import React from 'react';
import { History } from 'lucide-react';

export interface OutcomeEntry {
    outcome: string | null;
    by: string | null;
    at: string | null;   // null: recorded only as what a later change replaced
}

function shortDate(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function entry(e: OutcomeEntry): string {
    const who = [e.by, e.at ? shortDate(e.at) : null].filter(Boolean).join(', ');
    return who ? `${e.outcome || 'No outcome'} — ${who}` : `${e.outcome || 'No outcome'}`;
}

/**
 * The customer's earlier outcomes, and outcomes other agents saved on the timeline since
 * the current one. A Retention hand-over, a repeat cart or a non-owner's call changes
 * what the header shows, but nothing is deleted -- this line says so, with names and
 * dates, so "my disposition got wiped" can be answered on the spot.
 */
const OutcomeHistoryStrip: React.FC<{
    previous?: OutcomeEntry[] | null;
    others?: OutcomeEntry[] | null;
}> = ({ previous, others }) => {
    const prev = previous ?? [];
    const oth = others ?? [];
    if (prev.length === 0 && oth.length === 0) return null;
    return (
        <div className="px-4 py-1.5 border-b border-gray-200 bg-white text-[12px] text-gray-600 flex flex-col gap-0.5">
            {prev.length > 0 && (
                <div className="flex items-start gap-2">
                    <History className="w-3.5 h-3.5 shrink-0 mt-0.5 text-gray-400" />
                    <span>
                        <span className="font-semibold text-gray-700">Previous outcomes:</span>{' '}
                        {prev.map(entry).join('  ·  ')}
                    </span>
                </div>
            )}
            {oth.length > 0 && (
                <div className="flex items-start gap-2 pl-[22px]">
                    <span>
                        <span className="font-semibold text-gray-700">Also spoke to this customer:</span>{' '}
                        {oth.map(entry).join('  ·  ')}
                        <span className="text-gray-400"> (the owner's outcome above is unchanged)</span>
                    </span>
                </div>
            )}
        </div>
    );
};

export default OutcomeHistoryStrip;
